import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import { InvoiceActionSchema } from 'modules/AtRisk/api/use-load-unhandled-client';
import { isDefined } from 'remeda';
import { addResourceNameToQueryResult } from 'shared/hooks/utils/add-resource-name-to-query-result';
import { useAxiosInstance } from 'shared/hooks/utils/axios-instance';
import { handleZodError } from 'shared/hooks/utils/handle-zod-error';
import type { ApiError, QueryOptions } from 'shared/hooks/utils/types';
import { z } from 'zod/v4';

const AiSummarySchema = z
  .object({
    id: z.uuid(),
    summary: z
      .object({
        analysis: z.string(),
        overallAssessment: z.string(),
        recommendedActions: z.array(InvoiceActionSchema),
        followUpStrategy: z.string().optional(),
      })
      .nullable(),
    createdAt: z.coerce.date().optional(),
    rating: z.boolean().nullable(),
    isOutdated: z.boolean(),
  })
  .nullable();

export type AiSummary = z.infer<typeof AiSummarySchema>;

const LoadAiSummaryVariablesSchema = z.object({
  id: z.string(),
});

export type LoadAiSummaryVariables = z.infer<typeof LoadAiSummaryVariablesSchema>;

export async function loadDebtorAiSummaryQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: LoadAiSummaryVariables,
) {
  const instance = await axiosInstance;
  const { data } = await instance.get(`/debtors/${variables.id}/ai_summary`);

  const parseResult = AiSummarySchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'aiSummary');
    throw new SilentError();
  }

  return parseResult.data;
}

const emptyAiSummary: AiSummary = {
  id: '',
  summary: {
    analysis: '',
    overallAssessment: '',
    recommendedActions: [],
  },
  createdAt: new Date(),
  rating: null,
  isOutdated: false,
};

export function useLoadDebtorAiSummary(
  variables: LoadAiSummaryVariables,
  options: QueryOptions<AiSummary> = {},
) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<AiSummary, ApiError>({
    queryKey: ['aiSummary', variables],
    queryFn: () => loadDebtorAiSummaryQueryFn(axiosInstance, variables),
    retryOnMount: false,
    placeholderData: previousData => {
      if (isDefined(previousData)) return previousData;
      return emptyAiSummary;
    },
    ...options,
  });

  return addResourceNameToQueryResult<AiSummary, ApiError, 'aiSummary', 'PreventUndefined'>(
    'aiSummary',
    queryResult,
    emptyAiSummary,
  );
}
